/**
 * CONFIGURACIÓN DE LA PREVENTA
 * 
 * Este archivo te permite configurar la fecha de inicio, duración y precios
 * de la preventa para propósitos de desarrollo y pruebas.
 */

import { IS_TEST_MODE } from '../contracts/addresses';

// ================================================================
// CONFIGURACIÓN PRINCIPAL - MODIFICA ESTOS VALORES SEGÚN NECESITES
// ================================================================

// Fecha real de inicio de la preventa en modo producción (1 de junio de 2025 a las 10:00 UTC)
// Será las 6:00 AM en República Dominicana (UTC-4)
const REAL_PRESALE_START_DATE = "01-06-2025 10:00";

// Fecha de inicio de la preventa para pruebas (UTC+0)
// Formato: DD-MM-AAAA HH:MM (0 UTC)
// Ejemplo: "23-02-2025 10:00" significa 23 de febrero de 2025 a las 10:00 UTC
// Esto es lo que tú modificas: la fecha en formato fácil de leer
export const PRESALE_START_DATE_DISPLAY = "01-06-2025 10:00"; // Formato visual para ti

// Usar la fecha correcta según el modo
const dateToUse = IS_TEST_MODE ? PRESALE_START_DATE_DISPLAY : REAL_PRESALE_START_DATE;

// NO MODIFICAR ESTA LÍNEA - Se convierte automáticamente al formato que necesita JavaScript
// Estas líneas procesan automáticamente la fecha que escribiste arriba
const [datePart, timePart] = dateToUse.split(" ");
const [day, month, year] = datePart.split("-");
const [hours, minutes] = timePart.split(":");
// El resultado se usa aquí para crear un formato que JavaScript entienda
// Convertir al formato YYYY-MM-DDTHH:MM:SSZ para JavaScript
export const PRESALE_START_DATE = `${year}-${month}-${day}T${hours}:${minutes}:00Z`;

// Duración de cada etapa en días
export const STAGE_DURATION_DAYS = 30;

// Número total de etapas
export const TOTAL_STAGES = 5;

// Precios de tokens por etapa (en USDT) - Ahora son rangos de precios
export const STAGE_PRICES = [
  { minPrice: 0.00800, maxPrice: 0.00800, discount: "50% descuento" }, // Etapa 1
  { minPrice: 0.00870, maxPrice: 0.00870, discount: "40% descuento" }, // Etapa 2
  { minPrice: 0.00940, maxPrice: 0.00940, discount: "30% descuento" }, // Etapa 3
  { minPrice: 0.01060, maxPrice: 0.01060, discount: "20% descuento" }, // Etapa 4
  { minPrice: 0.01120, maxPrice: 0.01120, discount: "10% descuento" }, // Etapa 5
];

// ================================================================
// FUNCIONES DE CÁLCULO - NO MODIFICAR A MENOS QUE SEPAS LO QUE HACES
// ================================================================

// Cache para precios diarios, asegurando consistencia durante una sesión
const dailyPriceCache = {};

/**
 * Genera un precio aleatorio gradual que nunca disminuye
 * @param {number} minPrice - Precio mínimo para esta etapa
 * @param {number} maxPrice - Precio máximo para esta etapa
 * @param {number} daysSinceStart - Días transcurridos desde el inicio de preventa
 * @param {number} stageStartDay - Día en que inició la etapa actual
 * @returns {number} Precio generado para el día actual
 */
function generateDailyPrice(minPrice, maxPrice, daysSinceStart, stageStartDay) {
  // Clave para el caché basada en el día
  const cacheKey = Math.floor(daysSinceStart);
  
  // Si ya calculamos el precio para hoy, lo retornamos
  if (dailyPriceCache[cacheKey]) {
    return dailyPriceCache[cacheKey];
  }
  
  // Para el primer día de la etapa, usar el precio mínimo
  if (daysSinceStart <= stageStartDay) {
    dailyPriceCache[cacheKey] = minPrice;
    return minPrice;
  }
  
  // Obtener el precio de ayer
  const yesterdayPrice = generateDailyPrice(minPrice, maxPrice, daysSinceStart - 1, stageStartDay);
  
  // Calcular un incremento aleatorio (entre 0% y 0.5% del rango)
  const maxIncrement = (maxPrice - minPrice) * 0.02;
  const randomIncrement = Math.random() * maxIncrement;
  
  // Asegurar que el precio no exceda el máximo de la etapa
  const newPrice = Math.min(yesterdayPrice + randomIncrement, maxPrice);
  
  // Redondear a 5 decimales para una variación más sutil
  const roundedPrice = Math.round(newPrice * 100000) / 100000;
  
  // Guardar en caché para futuras consultas
  dailyPriceCache[cacheKey] = roundedPrice;
  
  return roundedPrice;
}

/**
 * Calcula el estado actual de la preventa basado en la fecha configurada
 * @returns {Object} Información detallada sobre el estado de la preventa
 */
export function calculatePresaleStatus() {
  // Fecha actual en UTC
  const now = new Date();
  
  // Fecha de inicio y fin de la preventa
  const startDate = new Date(PRESALE_START_DATE);
  const totalDurationMs = TOTAL_STAGES * STAGE_DURATION_DAYS * 24 * 60 * 60 * 1000;
  const endDate = new Date(startDate.getTime() + totalDurationMs);
  
  // Verificar si la preventa no ha comenzado
  if (now < startDate) {
    return {
      status: "pending",
      message: "presaleBox.presalePending", // Ahora usando clave de traducción
      stage: 0,
      currentPrice: STAGE_PRICES[0].minPrice,
      currentDiscount: STAGE_PRICES[0].discount,
      timeUntilStart: startDate - now,
      timeUntilNextStage: startDate - now,
      nextStageDate: startDate,
      progress: 0
    };
  }
  
  // Verificar si la preventa ya terminó
  if (now > endDate) {
    return {
      status: "completed",
      message: "presaleBox.presaleCompleted", // Ahora usando clave de traducción
      messageColor: "#ff0000",
      stage: TOTAL_STAGES,
      currentPrice: STAGE_PRICES[TOTAL_STAGES - 1].maxPrice,
      currentDiscount: STAGE_PRICES[TOTAL_STAGES - 1].discount,
      timeUntilStart: 0,
      timeUntilNextStage: 0,
      nextStageDate: null,
      progress: 100
    };
  }
  
  // Calcular días transcurridos desde el inicio
  const daysSinceStart = (now - startDate) / (24 * 60 * 60 * 1000);
  const currentStage = Math.min(Math.floor(daysSinceStart / STAGE_DURATION_DAYS) + 1, TOTAL_STAGES);
  
  // Calcular el día de inicio de la etapa actual
  const stageStartDay = (currentStage - 1) * STAGE_DURATION_DAYS;
  
  // Calcular precio actual usando el algoritmo gradual
  const { minPrice, maxPrice, discount } = STAGE_PRICES[currentStage - 1];
  const currentPrice = generateDailyPrice(minPrice, maxPrice, daysSinceStart, stageStartDay);
  
  // Calcular la fecha de la próxima etapa
  const nextStageStart = new Date(startDate.getTime() + (currentStage * STAGE_DURATION_DAYS * 24 * 60 * 60 * 1000));
  
  // Calcular el progreso de la etapa actual (0-100%)
  const stageStartDate = new Date(startDate.getTime() + ((currentStage - 1) * STAGE_DURATION_DAYS * 24 * 60 * 60 * 1000));
  const stageEndDate = nextStageStart;
  const totalStageDuration = stageEndDate - stageStartDate;
  const elapsedTime = now - stageStartDate;
  const progress = Math.min(Math.floor((elapsedTime / totalStageDuration) * 100), 100);
  
  return {
    status: "active",
    message: "presaleBox.buyMessage", // Cambiado a la clave de traducción
    stage: currentStage,
    currentPrice: currentPrice,
    currentDiscount: discount,
    timeUntilStart: 0,
    timeUntilNextStage: nextStageStart - now,
    nextStageDate: nextStageStart,
    progress: progress
  };
}

export default {
  PRESALE_START_DATE,
  PRESALE_START_DATE_DISPLAY,
  STAGE_DURATION_DAYS,
  TOTAL_STAGES,
  STAGE_PRICES,
  calculatePresaleStatus
};
